import React, { useState, useEffect, useCallback } from 'react';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import AddChemicalModal from '../components/AddChemicalModal';
import axios from 'axios';
import { GET_MASTER_DATA, BASE_URL } from '../utlilities/config';

// Icons
import { 
  FiSearch, FiPlus, FiTrash2, FiRefreshCw, 
  FiEdit, FiCheckCircle,
  FiFileText, FiAlertCircle
} from "react-icons/fi";
import { SiMoleculer } from "react-icons/si";

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading lab data...</p>
    </div>
  </div>
);

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black-400">
      <FiFileText size={32} />
    </div>
    <h3 className="text-lg font-medium text-black-700">No chemicals found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search or add a new chemical</p>
  </div>
);

const ChemicalMaster = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [chemicals, setChemicals] = useState([]);
  const [selectedChemicalId, setSelectedChemicalId] = useState(null);
  const [chemicalName, setChemicalName] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const getData = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MASTER_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setLoading(false);
        console.log('response',response.data.data);
        // Filter by Type 'C' and limit to first 2000 records
        const filteredData = (response.data.data || []).filter(item => item.Type === 'C');
        const limitedData = filteredData.slice(0, 2000);
        setChemicals(limitedData);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch labs data:', error);
      toast.error('Could not load labs data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const handleClick = () => {
    setShowModal(true);
  };

  const handleEditClick = (id, name) => {
    setChemicalName(name);
    setSelectedChemicalId(id);
    setShowModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeleteClick = (id) => {
    Swal.fire({
      title: 'Delete Chemical?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 mr-2',
        popup: 'rounded-xl shadow-xl'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setChemicals(chemicals.filter(c => c.id !== id));
        toast.success('Chemical successfully deleted.', {
          autoClose: 1000,
          icon: <FiCheckCircle className="text-green-500" />
        });
      }
    });
  };

  const handleRefresh = () => {
    getData();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedChemicalId(null);
  };

  // Filter data based on search query
  const filteredChemicals = chemicals.filter(chemical => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      chemical.Name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {/* <LeftMenu isExpanded={isMenuExpanded} toggleMenu={toggleMenu} /> */}
      
      <div className="p-6 flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-black-800 flex items-center">
              Chemical Master
            </h1>
            
            <button 
              className="px-4 py-1 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
              onClick={handleClick}
            >
              <FiPlus className="mr-2" /> Add New Chemical
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex flex-wrap justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:w-72">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black-400">
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                placeholder="Search by chemical name"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-black">
          </div>
          
          <button 
            onClick={handleRefresh}
            className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            <FiRefreshCw className="mr-1" size={14} /> Refresh
          </button>
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden">
          {filteredChemicals.length > 0 ? (
            <div className="overflow-x-auto h-full">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">
                      Chemical Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                
                <tbody className="divide-y divide-gray-100">
                  {filteredChemicals.map((chemical, index) => (
                    <tr 
                      key={chemical.id} 
                      className={`${
                        hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } transition-colors hover:bg-indigo-50`}
                      onMouseEnter={() => setHoverRow(index)}
                      onMouseLeave={() => setHoverRow(null)}
                    >
                      <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                        {chemical.Name}
                      </td>
                      
                      <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                        <div className="flex space-x-3">
                          <button
                            className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                            onClick={() => handleEditClick(chemical.ID, chemical.Name)}
                            title="Edit chemical"
                          >
                            <FiEdit size={16} />
                          </button>
                          
                          <button
                            className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                            onClick={() => handleDeleteClick(chemical.id)}
                            title="Delete chemical"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData />
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showModal && (
        <AddChemicalModal 
          setShowModal={handleModalClose} 
          chemicalName={chemicalName}
          onSave={handleRefresh} 
          chemicalId={selectedChemicalId}
          setSelectedChemicalId={setSelectedChemicalId}
          setChemicalName={setChemicalName}
        />
      )}
    </div>
  );
};

export default withAuth(ChemicalMaster);
